import React, { useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  Heading,
  HStack,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { useParams } from "react-router-dom";
import { getNetworkReportFactorMatrix } from "../networkReportApi";
import { formatNumber } from "../networkReportFormatters";

const Score = ({ value }) => (
  <Box
    as="span"
    display="inline-block"
    minW="3.25rem"
    px={2}
    py={1}
    borderRadius="md"
    bg="gray.100"
    color="gray.800"
    fontWeight="semibold"
    textAlign="center"
  >
    {value === null || value === undefined ? "—" : formatNumber(value)}
  </Box>
);

const NetworkReportSchoolsFactorsTab = () => {
  const { networkId } = useParams();
  const toast = useToast();
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedColor = useColorModeValue("gray.600", "gray.400");
  const stickyBg = useColorModeValue("white", "gray.800");
  const scoreBg = useColorModeValue("gray.100", "gray.700");
  const scoreColor = useColorModeValue("gray.800", "gray.100");
  const [matrix, setMatrix] = useState(null);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("input");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    setMatrix(null);
    setSelectedCategoryKey("input");

    getNetworkReportFactorMatrix(networkId)
      .then((result) => {
        if (!active) return;
        setMatrix(result);
        setLoading(false);
      })
      .catch((requestError) => {
        if (!active) return;
        console.error("Error loading Schools × Factors matrix:", requestError);
        setError("Could not load the Schools × Factors report.");
        setLoading(false);
        toast({ title: "Could not load the Schools × Factors report", status: "error", duration: 4000, isClosable: true });
      });

    return () => {
      active = false;
    };
  }, [networkId, toast]);

  const categories = matrix?.categories || [];
  const schools = matrix?.schools || [];
  const selectedCategory = categories.find(({ key }) => key === selectedCategoryKey)
    || categories[0]
    || null;
  const factors = selectedCategory?.factors || [];

  const schoolResults = new Map(schools.map((school) => [
    school.id,
    new Map(school.factorResults.map((result) => [String(result.factorId), result.stabilityIndex])),
  ]));

  if (loading) {
    return <HStack justify="center" py={12}><Spinner size="lg" label="Loading Schools × Factors report" /></HStack>;
  }

  if (error) {
    return <Alert status="error" borderRadius="md"><AlertIcon />{error}</Alert>;
  }

  return (
    <VStack align="stretch" spacing={4} minW={0}>
      <Box>
        <Heading size="md">Schools × Factors</Heading>
        <Text mt={1} color={mutedColor}>
          Compare every School across the Factors in the selected category.
        </Text>
      </Box>

      <ButtonGroup isAttached={false} spacing={2} flexWrap="wrap" aria-label="Factor category">
        {categories.map((category) => (
          <Button
            key={category.key}
            size="sm"
            minH="40px"
            variant={selectedCategory?.key === category.key ? "solid" : "outline"}
            colorScheme={selectedCategory?.key === category.key ? "blue" : "gray"}
            aria-pressed={selectedCategory?.key === category.key}
            onClick={() => setSelectedCategoryKey(category.key)}
          >
            {category.name}
          </Button>
        ))}
      </ButtonGroup>

      {schools.length === 0 && categories.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={2}>No Network data</Heading>
            <Text color={mutedColor}>There is no School or Factor data available for this Network yet.</Text>
          </CardBody>
        </Card>
      ) : schools.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={2}>No Schools</Heading>
            <Text color={mutedColor}>There are no Schools in this Network yet.</Text>
          </CardBody>
        </Card>
      ) : factors.length === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Heading size="sm" mb={2}>No Factors</Heading>
            <Text color={mutedColor}>No Factors are available in the selected category.</Text>
          </CardBody>
        </Card>
      ) : (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} minW={0}>
          <CardBody p={0}>
            <Box overflowX="auto" maxW="100%" data-testid="schools-factors-scroll-container">
              <Table variant="simple" size="sm" minW="max-content">
                <Thead>
                  <Tr>
                    <Th position="sticky" left={0} zIndex={2} bg={stickyBg} minW="12rem">School</Th>
                    {factors.map((factor) => (
                      <Th key={factor.id} minW="10rem" maxW="14rem" whiteSpace="normal">
                        {factor.name}
                      </Th>
                    ))}
                    <Th minW="7rem">Overall</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {schools.map((school) => {
                    const results = schoolResults.get(school.id) || new Map();
                    return (
                      <Tr key={school.id}>
                        <Td position="sticky" left={0} zIndex={1} bg={stickyBg} fontWeight="semibold" maxW="16rem" whiteSpace="normal">
                          {school.organizationName}
                        </Td>
                        {factors.map((factor) => (
                          <Td key={factor.id} textAlign="center">
                            <Box sx={{ "& > span": { bg: scoreBg, color: scoreColor } }}>
                              <Score value={results.get(String(factor.id))} />
                            </Box>
                          </Td>
                        ))}
                        <Td textAlign="center">
                          <Box sx={{ "& > span": { bg: scoreBg, color: scoreColor } }}>
                            <Score value={school.overallStabilityIndex} />
                          </Box>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      )}
    </VStack>
  );
};

export default NetworkReportSchoolsFactorsTab;
